"use client";

import Link from "next/link";
import { ArrowLeft, CheckCircle, AlertCircle, Phone, Ruler, Award } from "lucide-react";
import { useContactInfo } from '@/context/SiteConfigContext';

export default function ReturnsClient() {
  const contactInfo = useContactInfo();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-white border-b border-gray-200 py-8 sm:py-10">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link href="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Returns & Guarantees</h1>
          <p className="text-gray-600 text-lg">
            We stand behind every product. If something isn't right, we'll make it right.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-4xl py-8 sm:py-10">
        {/* Trust Badges */}
        <div className="grid grid-cols-2 gap-4 mb-10">
          <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
            <Ruler className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-900 text-sm">Perfect Fit Guarantee</h3>
            <p className="text-gray-500 text-xs mt-0.5">Free remakes</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
            <Award className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-900 text-sm">Satisfaction Guarantee</h3>
            <p className="text-gray-500 text-xs mt-0.5">30-day coverage</p>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-5">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="flex gap-3">
              <span className="w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">1</span>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Contact Us</h3>
                <p className="text-gray-600 text-sm mt-0.5">Call or email within 30 days of delivery with your order number.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">2</span>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">We Review</h3>
                <p className="text-gray-600 text-sm mt-0.5">We'll assess the issue and determine the best solution — remake, replacement, or credit.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">3</span>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Resolution</h3>
                <p className="text-gray-600 text-sm mt-0.5">We ship your replacement at no cost. Return shipping on defective items is on us.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Perfect Fit Guarantee */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
          <div className="bg-blue-600 text-white px-6 py-4 flex items-center gap-3">
            <Ruler className="w-6 h-6 shrink-0" />
            <div>
              <h2 className="text-lg font-bold">Perfect Fit Guarantee</h2>
              <p className="text-blue-100 text-sm">Made a measuring mistake? We'll remake them free.</p>
            </div>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">What's Covered</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                    Free remake for measuring errors
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                    Free return shipping on old blinds
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                    Size and mount type changes
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                    Valid within 30 days of delivery
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">Conditions</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    Same product type only (no style changes)
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    1 remake per item
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    Price difference may apply for larger sizes
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Satisfaction Guarantee */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
          <div className="bg-green-600 text-white px-6 py-4 flex items-center gap-3">
            <Award className="w-6 h-6 shrink-0" />
            <div>
              <h2 className="text-lg font-bold">Satisfaction Guarantee</h2>
              <p className="text-green-100 text-sm">Not satisfied? Contact us within 30 days.</p>
            </div>
          </div>
          <div className="p-6">
            <ul className="space-y-2 text-sm text-gray-600 mb-4">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                Defects in material or workmanship — free replacement
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                Product doesn't function properly — free replacement
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                Wrong size due to production error — free replacement
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                Color or style not as expected — remake available
              </li>
            </ul>
            <p className="text-sm text-gray-500">
              Custom-made products cannot be returned for a refund, but we'll work with you to find a solution you're happy with.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gray-900 rounded-lg p-6 sm:p-8 text-center text-white">
          <h2 className="text-xl font-bold mb-2">Need Help With a Return?</h2>
          <p className="text-gray-300 mb-5 text-sm">
            Our team is ready to help with returns and remakes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors text-sm"
            >
              Contact Support
            </Link>
            <a
              href={contactInfo.phoneTel}
              className="inline-flex items-center gap-2 text-white font-semibold hover:underline text-sm"
            >
              <Phone className="w-4 h-4" />
              {contactInfo.phone}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
